import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { GatewayPlugin } from '../types/plugin';

interface PluginDetailDialogProps {
  plugin: GatewayPlugin;
  displayName: string;
  onClose: () => void;
}

/** 플러그인 상세 read-only 다이얼로그 (#621) — 전체 설명 + flagProperty + G코드를 표시한다. */
export function PluginDetailDialog({ plugin, displayName, onClose }: PluginDetailDialogProps) {
  const { t } = useTranslation();
  const closeBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    const id = requestAnimationFrame(() => closeBtnRef.current?.focus());
    return () => {
      document.removeEventListener('keydown', handler);
      cancelAnimationFrame(id);
    };
  }, [onClose]);

  return (
    <div
      role="presentation"
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={displayName}
        onClick={(e) => e.stopPropagation()}
        style={{ background: 'var(--color-surface-background)', borderRadius: 'var(--radius-lg)', padding: 'var(--spacing-6)', minWidth: '480px', maxWidth: '640px', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: 'var(--font-size-lg)' }}>{displayName}</h2>
          <button ref={closeBtnRef} type="button" onClick={onClose} style={closeBtnStyle} aria-label={t('gateway.shared.actions.close')}>
            &times;
          </button>
        </div>

        <dl style={dlStyle}>
          <dt style={dtStyle}>{t('gateway.plugins.columns.key')}</dt>
          <dd style={ddMonoStyle}>{plugin.key}</dd>

          <dt style={dtStyle}>{t('gateway.plugins.columns.flagProperty')}</dt>
          <dd style={ddMonoStyle}>{plugin.flagProperty}</dd>

          <dt style={dtStyle}>{t('gateway.plugins.columns.gCode')}</dt>
          <dd style={ddMonoStyle}>{plugin.gCode}</dd>
        </dl>

        <p style={{ marginTop: 'var(--spacing-4)', fontSize: 'var(--font-size-sm)', lineHeight: 1.6 }}>
          {plugin.description}
        </p>
      </div>
    </div>
  );
}

const dlStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '140px 1fr',
  rowGap: 'var(--spacing-2)',
  columnGap: 'var(--spacing-3)',
  margin: 'var(--spacing-4) 0 0',
};

const dtStyle: React.CSSProperties = { fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' };
const ddMonoStyle: React.CSSProperties = { margin: 0, fontSize: 'var(--font-size-sm)', fontFamily: 'var(--font-family-mono)' };

const closeBtnStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  fontSize: 'var(--font-size-xl, 24px)',
  lineHeight: 1,
  cursor: 'pointer',
  color: 'var(--color-text-secondary)',
};
